import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Star,
  RefreshCw,
  AlertTriangle,
  PauseCircle,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Panel } from "@/components/Panel";
import { RiskGauge } from "@/components/RiskGauge";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useSentinel } from "@/context/SentinelContext";
import {
  locationStatus,
  systemStatus,
  formatTimestamp,
} from "@/data/mockData";
import { cn } from "@/lib/utils";

const SENSING_KEYS = ["accelerometer", "gyroscope", "microphone", "gps"];

const STATE_COPY = {
  HIGH: {
    headline: "Elevated risk detected in the latest incident.",
    body: "Awaiting user verification. Review the active incident and emergency response.",
  },
  MEDIUM: {
    headline: "Abnormal pattern detected in the latest incident.",
    body: "The situation is being monitored. No emergency escalation has been triggered.",
  },
  LOW: {
    headline: "No abnormal activity in the latest incident.",
    body: "Sensor context matches normal patterns. Guardian Mode is active.",
  },
  SAFE: {
    headline: "No abnormal activity detected.",
    body: "Sensor context matches normal patterns. Guardian Mode is active.",
  },
};

export default function Dashboard() {
  const { user } = useAuth();
  const {
    guardianMode,
    monitoring,
    safetyState,
    riskScore,
    loading,
    error,
    refresh,
    contacts,
    incidents,
    latestIncident,
    live,
  } = useSentinel();

  const recent = incidents.slice(0, 4);
  const copy = STATE_COPY[safetyState] || STATE_COPY.SAFE;

  return (
    <DashboardLayout>
      {/* Page header */}
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-100">
            Safety Overview
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Welcome back, {user?.name}.{" "}
            {monitoring
              ? live
                ? "Live incident data from Supabase."
                : "Connecting to Supabase…"
              : "Monitoring is paused."}
          </p>
        </div>
        <span
          className="flex w-fit items-center gap-2 rounded-md border border-sentinel-border bg-sentinel-deep px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400"
          data-testid="dashboard-status-indicator"
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              monitoring ? "animate-pulse bg-emerald-400" : "bg-slate-600"
            )}
          />
          {loading
            ? "Syncing…"
            : !monitoring
            ? "Paused"
            : live
            ? "Live · Supabase"
            : "Reconnecting…"}
        </span>
      </div>

      {error && !latestIncident ? (
        <ErrorState message={error} onRetry={refresh} />
      ) : loading && !latestIncident ? (
        <DashboardSkeleton />
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
            {/* Current safety state + gauge */}
            <Panel title="Current Safety State" className="lg:col-span-2" data-testid="safety-state-panel">
              <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-center sm:gap-10">
                <div className={cn("relative", !monitoring && "opacity-40")}>
                  <RiskGauge score={riskScore} size={200} />
                  {!monitoring && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="rounded bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300">
                        Paused
                      </span>
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  {monitoring ? (
                    <RiskBadge
                      level={safetyState}
                      label={latestIncident?.rawLevel}
                      className="text-xs"
                      data-testid="current-safety-state-badge"
                    />
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 rounded border border-sentinel-border bg-sentinel-deep px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400"
                      data-testid="current-safety-state-badge"
                    >
                      <PauseCircle className="h-3 w-3" /> Monitoring paused
                    </span>
                  )}
                  <p className="mt-4 text-lg font-medium text-slate-100">
                    {monitoring ? copy.headline : "Guardian Mode is off."}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {monitoring
                      ? copy.body
                      : "Enable Guardian Mode from the header to resume risk evaluation and emergency detection."}
                  </p>

                  {/* Latest incident — sensor values from public.incidents */}
                  {monitoring && latestIncident?.sensors?.length > 0 && (
                    <div className="mt-5">
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        Latest incident · {latestIncident.id} · sensor values
                      </p>
                      <div
                        className="grid grid-cols-3 gap-3"
                        data-testid="latest-incident-sensors"
                      >
                        {latestIncident.sensors.map((s) => (
                          <Metric key={s.key || s.sensor} label={s.sensor} value={s.reading} />
                        ))}
                      </div>
                    </div>
                  )}

                  <p
                    className="mt-4 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500"
                    data-testid="latest-incident-timestamp"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Live · Supabase · Recorded {formatTimestamp(latestIncident?.timestamp)}
                  </p>
                  {monitoring && safetyState === "HIGH" && (
                    <Link
                      to="/emergency"
                      data-testid="dashboard-view-emergency-link"
                      className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-red-400 hover:text-red-300"
                    >
                      View emergency response <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            </Panel>

            {/* Guardian Mode */}
            <Panel title="Guardian Mode" data-testid="guardian-mode-panel">
              <div className="flex h-full flex-col items-center justify-center py-2 text-center">
                <div
                  className={cn(
                    "flex h-24 w-24 items-center justify-center rounded-full border",
                    guardianMode
                      ? "border-emerald-500/40 bg-emerald-500/10"
                      : "border-sentinel-border bg-sentinel-deep"
                  )}
                >
                  <ShieldCheck
                    className={cn("h-10 w-10", guardianMode ? "text-emerald-400" : "text-slate-600")}
                    strokeWidth={1.6}
                  />
                </div>
                <p
                  className={cn(
                    "mt-4 font-mono text-2xl font-bold tracking-widest",
                    guardianMode ? "text-emerald-400" : "text-slate-500"
                  )}
                  data-testid="guardian-mode-status-large"
                >
                  {guardianMode ? "ON" : "OFF"}
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Status:{" "}
                  <span className={guardianMode ? "text-emerald-400" : "text-slate-300"}>
                    {guardianMode ? "Monitoring active" : "Paused"}
                  </span>
                </p>
                <p className="mt-4 font-mono text-[11px] text-slate-600">Toggle from the header control</p>
              </div>
            </Panel>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
            {/* Recent incidents */}
            <Panel
              title="Recent Incidents"
              className="lg:col-span-2"
              data-testid="recent-incidents-panel"
              contentClassName="p-0"
              action={
                <Link
                  to="/incidents"
                  className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-sentinel-accent hover:text-sentinel-accenthover"
                  data-testid="view-all-incidents-link"
                >
                  View all <ArrowRight className="h-3 w-3" />
                </Link>
              }
            >
              {recent.length === 0 ? (
                <EmptyState label="No incidents recorded yet." />
              ) : (
                <ul className="divide-y divide-sentinel-border">
                  {recent.map((inc) => (
                    <li key={inc.id}>
                      <Link
                        to={`/incidents/${inc.id}`}
                        data-testid={`dashboard-incident-${inc.id}`}
                        className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[#1a1e2c] focus:outline-none focus-visible:bg-[#1a1e2c]"
                      >
                        <RiskBadge level={inc.level} showDot={false} className="w-[86px] justify-center" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-100">{inc.eventType}</p>
                          <p className="font-mono text-[11px] text-slate-500">{formatTimestamp(inc.timestamp)}</p>
                        </div>
                        <span className="hidden font-mono text-lg font-bold tabular text-slate-300 sm:block">
                          {inc.score}
                        </span>
                        <span className="w-24 text-right font-mono text-[11px] uppercase tracking-wider text-slate-500">
                          {inc.status}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Location status */}
            <Panel title="Current Location" data-testid="location-panel">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
                  <MapPin className="h-4 w-4 text-sentinel-accent" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100">{locationStatus.label}</p>
                  <p className="mt-0.5 font-mono text-xs text-slate-500">{locationStatus.note}</p>
                </div>
              </div>
              <div className="mt-5 space-y-2.5 border-t border-sentinel-border pt-4">
                <KV label="Status" value={monitoring ? locationStatus.statusLabel : "Paused"} ok={false} />
                <KV label="Accuracy" value={locationStatus.accuracy} />
                <KV label="Source" value={locationStatus.source} />
                <KV label="Updated" value={locationStatus.updated} />
              </div>
            </Panel>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
            {/* Emergency contacts */}
            <Panel
              title="Emergency Contacts"
              data-testid="dashboard-contacts-panel"
              contentClassName="p-0"
              action={
                <Link
                  to="/contacts"
                  className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-sentinel-accent hover:text-sentinel-accenthover"
                  data-testid="manage-contacts-link"
                >
                  Manage <ArrowRight className="h-3 w-3" />
                </Link>
              }
            >
              {contacts.length === 0 ? (
                <EmptyState
                  label="No emergency contacts."
                  action={
                    <Link to="/contacts" className="font-mono text-[11px] uppercase tracking-widest text-sentinel-accent hover:text-sentinel-accenthover">
                      Add a contact
                    </Link>
                  }
                />
              ) : (
                <>
                  <ul className="divide-y divide-sentinel-border">
                    {[...contacts]
                      .sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1))
                      .slice(0, 4)
                      .map((c) => (
                        <li key={c.id}>
                          <Link
                            to="/contacts"
                            data-testid={`dashboard-contact-${c.id}`}
                            className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-[#1a1e2c] focus:outline-none focus-visible:bg-[#1a1e2c]"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded bg-sentinel-deep font-mono text-[11px] font-semibold text-slate-300">
                              {c.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="flex items-center gap-1.5 truncate text-sm text-slate-100">
                                {c.name}
                                {c.primary && <Star className="h-3 w-3 fill-sentinel-accent text-sentinel-accent" />}
                              </p>
                              <p className="font-mono text-[11px] text-slate-500">{c.relationship || "—"}</p>
                            </div>
                            <span className="hidden font-mono text-xs text-slate-400 sm:block">{c.phone}</span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                  {contacts.length > 4 && (
                    <Link
                      to="/contacts"
                      data-testid="dashboard-contacts-overflow"
                      className="block border-t border-sentinel-border px-5 py-2.5 text-center font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors hover:text-sentinel-accent"
                    >
                      +{contacts.length - 4} more contacts
                    </Link>
                  )}
                </>
              )}
            </Panel>

            {/* System status */}
            <Panel title="System Status" className="lg:col-span-2" data-testid="system-status-panel">
              <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {systemStatus.map((s) => {
                  const standby = !guardianMode && SENSING_KEYS.includes(s.key);
                  const state = standby ? "Standby" : s.state;
                  return (
                    <div key={s.key} className="flex items-center justify-between border-b border-sentinel-border/60 pb-3">
                      <span className="flex items-center gap-2.5 text-sm text-slate-300">
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            standby ? "bg-amber-400" : s.ok ? "bg-emerald-400" : "bg-red-400"
                          )}
                        />
                        {s.label}
                      </span>
                      <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{state}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md border border-sentinel-border bg-sentinel-deep px-3 py-2.5">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold text-slate-100">{value}</p>
    </div>
  );
}

function KV({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <span className={cn("font-mono text-xs", ok ? "text-emerald-400" : "text-slate-300")}>{value}</span>
    </div>
  );
}

function EmptyState({ label, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-5 py-12 text-center">
      <p className="text-sm text-slate-500">{label}</p>
      {action}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div
      className="mt-6 flex flex-col items-center justify-center gap-4 rounded-lg border border-red-500/30 bg-[#160d10] px-6 py-14 text-center"
      data-testid="dashboard-error-state"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-red-500/40 bg-red-500/10">
        <AlertTriangle className="h-6 w-6 text-red-400" />
      </span>
      <div>
        <p className="font-heading text-lg font-semibold text-slate-100">Data unavailable</p>
        <p className="mt-1 max-w-sm text-sm text-slate-400">{message}</p>
      </div>
      <Button
        onClick={onRetry}
        data-testid="dashboard-retry-button"
        className="bg-sentinel-accent text-white hover:bg-sentinel-accenthover"
      >
        <RefreshCw className="mr-2 h-4 w-4" /> Retry
      </Button>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div data-testid="dashboard-loading-skeleton">
      <div className="mt-6 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
        <div className="rounded-lg border border-sentinel-border bg-sentinel-card p-5 lg:col-span-2">
          <div className="flex flex-col items-center gap-8 sm:flex-row">
            <Skeleton className="h-[200px] w-[200px] rounded-full bg-sentinel-border/40" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-5 w-28 bg-sentinel-border/40" />
              <Skeleton className="h-6 w-3/4 bg-sentinel-border/40" />
              <Skeleton className="h-4 w-full bg-sentinel-border/40" />
              <Skeleton className="h-4 w-2/3 bg-sentinel-border/40" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-sentinel-border bg-sentinel-card p-5">
          <div className="flex flex-col items-center gap-4 py-4">
            <Skeleton className="h-24 w-24 rounded-full bg-sentinel-border/40" />
            <Skeleton className="h-6 w-16 bg-sentinel-border/40" />
            <Skeleton className="h-4 w-40 bg-sentinel-border/40" />
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
        <div className="space-y-3 rounded-lg border border-sentinel-border bg-sentinel-card p-5 lg:col-span-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full bg-sentinel-border/40" />
          ))}
        </div>
        <div className="space-y-3 rounded-lg border border-sentinel-border bg-sentinel-card p-5">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-6 w-full bg-sentinel-border/40" />
          ))}
        </div>
      </div>
    </div>
  );
}
